/* World Landing Page — `/bookstore/world/:slug`.
 * One shared fictional universe (e.g. the Thorn Accord) spanning
 * multiple pen names: hero + lore blurb, every book in it, any bundle
 * built entirely from its books, and the authors who write in it.
 * Reached from a book's "Same World" shelf chip or its "More from
 * this world" strip in BookDetailModal.
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, ShoppingBag, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSeoMeta } from '@/lib/useSeoMeta';
import { bookstoreApi, resolveCoverUrl } from '@/lib/bookstoreApi';
import { useBookstoreAuth } from '@/lib/bookstoreAuthContext';
import { Money } from '@/contexts/CurrencyContext';
import BookCard from '@/components/bookstore/BookCard';
import BookDetailModal from '@/components/bookstore/BookDetailModal';
import AdultsOnlyGate, { hasAcceptedAdultsOnly } from '@/components/bookstore/AdultsOnlyGate';
import GenreContentWarningDialog, { hasAcceptedContentWarning } from '@/components/bookstore/GenreContentWarningDialog';

export default function WorldPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useBookstoreAuth();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(null);
  // iter-deeplink-gate (world) — re-evaluated on every bump, same
  // pattern GenrePage uses so a shared world link can't skip the gate.
  const [, setGateTick] = useState(0);
  const bumpGate = () => setGateTick((n) => n + 1);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    bookstoreApi.worldPage(slug)
      .then(setData)
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error('Could not load this collection right now. Please try again.');
      });
  }, [slug]);

  useSeoMeta(`/bookstore/world/${slug}`, {
    title: data ? `${data.world.name} — Crown & Rose` : 'Collection — Crown & Rose',
    description: data?.world?.tagline || undefined,
  });

  const goBack = () => {
    // Direct visits (shared link / new tab) have no in-app history to
    // go back to — `navigate(-1)` would leave the site entirely.
    if (location.key === 'default' || window.history.length <= 1) navigate('/bookstore');
    else navigate(-1);
  };

  // A world can (in principle) span more than one genre — gate if ANY
  // of them requires the 18+ checklist or carries a content warning.
  const genres = data?.genres || [];
  const needsAdultsOnlyGate = genres.some((g) => g.parent_group === 'adults-only') && !hasAcceptedAdultsOnly();
  const warnGenre = !needsAdultsOnlyGate
    ? genres.find((g) => g.content_warning && !hasAcceptedContentWarning(g.slug))
    : null;
  const gatePassed = !data || (!needsAdultsOnlyGate && !warnGenre);

  // iter-deeplink-gate-checklist — Only fetch the parent's custom
  // checklist when it's actually needed, mirroring GenrePage.
  const [parentChecklist, setParentChecklist] = useState(undefined);
  useEffect(() => {
    if (needsAdultsOnlyGate) {
      bookstoreApi.booksByGenre('adults-only')
        .then((r) => setParentChecklist(r?.genre?.gate_checklist))
        .catch(() => {});
    }
  }, [needsAdultsOnlyGate]);

  const startBundle = async (b) => {
    if (!user) {
      navigate(`/bookstore/login?next=/bookstore/world/${slug}`);
      return;
    }
    try {
      const { url } = await bookstoreApi.checkoutBundle(b.id, window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start bundle checkout.');
    }
  };

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background text-foreground" data-testid="bs-world-not-found">
        <p className="text-sm text-muted-foreground">We couldn't find that collection.</p>
        <button onClick={() => navigate('/bookstore')} className="text-sm text-primary underline underline-offset-4">
          Back to the bookstore
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background" data-testid="bs-world-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { world, books, bundles, authors } = data;

  return (
    <div className="min-h-screen bg-background text-foreground pb-[max(6rem,env(safe-area-inset-bottom))]" data-testid={`bs-world-page-${slug}`}>
      <AdultsOnlyGate
        open={needsAdultsOnlyGate}
        checklist={parentChecklist}
        onAccept={bumpGate}
        onRetreat={() => navigate('/bookstore')}
      />
      <GenreContentWarningDialog
        genre={(!needsAdultsOnlyGate && warnGenre) ? warnGenre : null}
        onAcceptOnce={bumpGate}
        onRetreat={() => navigate('/bookstore')}
      />
      <div className="border-b border-border/60 bg-card/40">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <button
            onClick={goBack}
            data-testid="bs-world-back-btn"
            className="inline-flex items-center gap-1 text-[11.5px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" /> Back
          </button>
          <Link to="/bookstore" data-testid="bs-world-all-genres-link" className="text-[11.5px] uppercase tracking-wider text-muted-foreground hover:text-foreground">
            All genres
          </Link>
        </div>
      </div>

      {!gatePassed ? (
        /* Dialogs above are open; hold the real content back until
           the reader clicks through, same as GenrePage's deep-link gate. */
        <div className="flex h-72 items-center justify-center text-muted-foreground" data-testid={`bs-world-gate-pending-${slug}`}>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking access…
        </div>
      ) : (
        <>
          {/* Hero */}
          <section
            className="relative overflow-hidden border-b border-border/60"
            style={world.hero_image_url ? {
              backgroundImage: `linear-gradient(180deg, rgba(10,6,8,0.55), rgba(10,6,8,0.88)), url(${world.hero_image_url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            } : undefined}
            data-testid="bs-world-hero"
          >
            <div className="mx-auto max-w-4xl px-6 py-16 text-center sm:py-20">
              <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary">
                <Link2 className="h-3 w-3" /> Shared World
              </div>
              <h1 className={`text-4xl font-semibold tracking-tight sm:text-5xl ${world.hero_image_url ? 'text-white drop-shadow-lg' : ''}`} data-testid="bs-world-name">
                {world.name}
              </h1>
              {world.tagline && (
                <p className={`mt-3 text-[15px] italic sm:text-base ${world.hero_image_url ? 'text-white/85' : 'text-muted-foreground'}`} data-testid="bs-world-tagline">
                  {world.tagline}
                </p>
              )}
              {world.lore && (
                <p className={`mx-auto mt-5 max-w-2xl text-[13.5px] leading-relaxed ${world.hero_image_url ? 'text-white/80' : 'text-muted-foreground'}`} data-testid="bs-world-lore">
                  {world.lore}
                </p>
              )}
            </div>
          </section>

          <section className="mx-auto max-w-6xl px-6 py-10">
            {/* Family Tree — one card per House/Warden pairing, so a
                reader can jump straight into whichever pairing hooks
                them instead of reading all 4 blurbs first. Only shows
                for books an admin has tagged with both labels. */}
            {books.some((b) => b.pairing_label) && (
              <div className="mb-10">
                <h2 className="mb-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">The pairings</h2>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="bs-world-family-tree">
                  {books.filter((b) => b.pairing_label).map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => setOpen(b)}
                      data-testid={`bs-world-pairing-${b.id}`}
                      className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card text-left transition hover:border-primary/50 hover:shadow-panel-active"
                    >
                      <div className="flex">
                        {b.cover_front_url && (
                          <img
                            src={resolveCoverUrl(b.cover_front_url)}
                            alt={b.title}
                            className="h-28 w-20 shrink-0 object-cover"
                          />
                        )}
                        <div className="flex min-w-0 flex-col justify-center gap-1 px-3 py-2">
                          {b.house_label && (
                            <span className="w-fit rounded-full bg-primary/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-primary">
                              {b.house_label}
                            </span>
                          )}
                          <span className="text-[12.5px] font-semibold leading-snug group-hover:underline">{b.pairing_label}</span>
                          <span className="truncate text-[10.5px] text-muted-foreground">{b.title}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Authors row */}
            {authors.length > 0 && (
              <div className="mb-10">
                <h2 className="mb-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Written by</h2>
                <div className="flex flex-wrap gap-4" data-testid="bs-world-authors-row">
                  {authors.map((a) => (
                    <button
                      key={a.slug}
                      type="button"
                      onClick={() => navigate(`/bookstore/author/${a.slug}`)}
                      data-testid={`bs-world-author-${a.slug}`}
                      className="group flex items-center gap-2 rounded-full border border-border bg-card py-1 pl-1 pr-3 text-left transition hover:border-primary/50 hover:shadow-panel-active"
                    >
                      {a.portrait_url ? (
                        <img src={a.portrait_url} alt={a.pen_name} className="h-9 w-9 rounded-full object-cover" />
                      ) : (
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                          {a.pen_name?.[0]}
                        </span>
                      )}
                      <span className="text-[12.5px] font-medium group-hover:underline">{a.pen_name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Books grid — each BookCard overflows its own box with a
                title/author/price strip (-bottom-11), so grid rows need
                extra bottom space reserved per cell or the next row's
                covers overlap that text. */}
            <div className="mb-16">
              <h2 className="mb-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Books in this world ({books.length})
              </h2>
              {books.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border p-6 text-[13px] text-muted-foreground">
                  No books published in this collection yet.
                </p>
              ) : (
                <div className="grid grid-cols-2 justify-items-center gap-x-4 gap-y-16 sm:grid-cols-3 lg:grid-cols-4" data-testid="bs-world-books-grid">
                  {books.map((b) => (
                    <BookCard key={b.id} book={b} onClick={setOpen} />
                  ))}
                </div>
              )}
            </div>

            {/* Bundle deals */}
            {bundles.length > 0 && (
              <div>
                <h2 className="mb-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Bundle deals</h2>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="bs-world-bundles">
                  {bundles.map((bd) => (
                    <button
                      key={bd.id}
                      type="button"
                      onClick={() => startBundle(bd)}
                      data-testid={`bs-world-bundle-${bd.id}`}
                      className="rounded-xl border border-border bg-card p-5 text-left transition hover:border-primary/50 hover:shadow-panel-active"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {bd.book_covers?.some(Boolean) && (
                            <div className="flex shrink-0 -space-x-2">
                              {bd.book_covers.filter(Boolean).slice(0, 4).map((url, i) => (
                                <img
                                  key={i}
                                  src={url}
                                  alt=""
                                  className="h-16 w-11 rounded-sm border border-border object-cover shadow-sm"
                                  style={{ zIndex: 10 - i }}
                                />
                              ))}
                            </div>
                          )}
                          <div>
                            <div className="text-[15px] font-semibold tracking-tight">{bd.title}</div>
                            <div className="text-[12px] text-muted-foreground">{bd.blurb}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-semibold text-primary">
                            <Money usdCents={bd.bundle_price_cents} overrides={bd.price_overrides} />
                          </div>
                          <div className="text-[11px] text-muted-foreground line-through">
                            <Money usdCents={bd.regular_price_cents} />
                          </div>
                          <div className="text-[10.5px] font-semibold text-success">{bd.savings_label}</div>
                        </div>
                      </div>
                      <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-primary">
                        <ShoppingBag className="h-3.5 w-3.5" /> Get the bundle
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>
        </>
      )}

      {open && <BookDetailModal book={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
